// A day's tradeable universe.
//
// `lib/tickets.js` throws away every bookmaker but one before it starts
// thinking — that is what makes Classic reproducible, and it is also why it
// can never see that TAB is paying 2.80 on the side Sportsbet has at 2.55.
// This module keeps the whole board: one row per event x bookmaker x outcome,
// folded into one Match per fixture with every price still attached.
//
// Rows are the canonical shape both the live feed and the historical CSVs
// reduce to, so a backtest and today's pad run through the same code.

import { localDate } from '../tickets.js';
import { asMatchLine } from './race-line.js';
import { devig } from './devig.js';
import { COMMISSION, isExchange, netPrice } from './exchange.js';
// Who owns which front door: a leaf table, so the soft board can be counted one
// vote per operator rather than one per brand (engine review #5).
import { operatorOf } from './operators.js';
import {
  SPORTS, isRacingSport, matchKey as makeMatchKey, sportFromOddsApiKey, sportLabel, teamId, slug,
} from './teams.js';

/** Selections a head-to-head market can carry, in the order devig sees them. */
export const SELECTIONS = ['home', 'away', 'draw'];

/**
 * The market a stored line is priced in: `win` for a race, `h2h` for a fixture.
 *
 * One expression, because the same question is asked in two places that must
 * not answer it differently — `fromHistoryMatches` here decides which price
 * rows to read, and `dataProfile` in backtest.js decides which ones count as a
 * book on the board. A stored racing line whose rows say `win` and a reader
 * that only knows `h2h` is a season that loads as empty and says nothing.
 */
export const marketOf = (match) => (match?.runners ? 'win' : 'h2h');

/**
 * The Odds API v4 response → canonical rows. One row per event x bookmaker x
 * h2h outcome, so nothing is dropped: an event in a sport we have no id for
 * keeps its raw `sport_key` rather than disappearing.
 *
 * THE BETFAIR EXCHANGE COMES THROUGH HERE TOO, and that is the whole of how it
 * is wired. `server/providers/betfairExchange.mjs` answers in this same event
 * shape — one bookmaker, `betfair_ex_au` — and `lib/odds.js` appends those
 * events to the board the punter already fetched. Two events for one fixture
 * then produce two sets of rows with the SAME matchKey, because the key is
 * built from `teamId` and the alias tables in teams.js resolve "Melbourne
 * Storm" and "Storm" to one id; `buildUniverse` folds them into one Match with
 * the exchange as one more column. Nothing here knows or needs to know that a
 * second provider was involved — which is why adding one costs no credits and
 * no second board.
 */
export function fromOddsApiEvents(events, { asOf }) {
  const rows = [];
  for (const event of events ?? []) {
    const sport = sportFromOddsApiKey(event.sport_key) ?? event.sport_key;
    const homeId = teamId(sport, event.home_team) ?? slug(event.home_team);
    const awayId = teamId(sport, event.away_team) ?? slug(event.away_team);
    const key = makeMatchKey(sport, event.commence_time, homeId, awayId);
    for (const book of event.bookmakers ?? []) {
      const h2h = book.markets?.find((m) => m.key === 'h2h');
      if (!h2h) continue;
      for (const outcome of h2h.outcomes ?? []) {
        const selection = outcome.name === event.home_team ? 'home'
          : outcome.name === event.away_team ? 'away'
            : outcome.name === 'Draw' ? 'draw' : null;
        if (!selection) continue;
        const link = outcome?.link ?? h2h?.link ?? book?.link ?? null;
        rows.push({
          matchKey: key,
          providerEventId: event.id,
          sport,
          competition: event.sport_title,
          kickoff: event.commence_time,
          home: { id: homeId, name: event.home_team },
          away: { id: awayId, name: event.away_team },
          selection,
          bookmaker: book.key,
          bookmakerName: book.title,
          price: outcome.price,
          // The book's own URL for this bet, where `includeLinks=true` brought
          // one back: the outcome's link is a BETSLIP link and the market's or
          // the bookmaker's is the page. Same three-step fallback the server's
          // `linkOf` makes (providers/canonical.mjs), because this file and
          // that one answer the same rows off the same events.
          ...(link ? { link, linkKind: outcome?.link ? 'betslip' : 'event' } : {}),
          // WHAT THIS BOOK OPENED THIS SIDE AT, where the server knows. The day
          // route stamps it onto the outcome off the first capture in the day's
          // drift series (`stampOpenings`, server/odds.mjs), so the move since
          // the open costs the app no call and the punter no credit. Absent on
          // a board stored before the series existed and on every provider that
          // does not keep one, and a leg then prints nothing about the open
          // rather than guessing at it.
          ...(Number.isFinite(outcome?.openingPrice)
            ? { openingPrice: outcome.openingPrice, openingAt: outcome.openingAt ?? null }
            : {}),
          liquidity: null,
          capturedAt: asOf,
          kind: 'live',
        });
      }
    }
  }
  return rows;
}

/** `open+2h`, `open-30m`, `open`, `open+1d` — an offset from the first capture. */
const OFFSET_RE = /^open(?:\s*([+-])\s*(\d+(?:\.\d+)?)\s*([mhd]))?$/i;

const UNIT_MS = { m: 60_000, h: 3_600_000, d: 86_400_000 };

/**
 * WHEN A BACKTEST IS STANDING, for `priceKind: 'at'`:
 *
 *   'open+2h'                 two hours after this match's FIRST capture
 *   'open'                    the first capture itself
 *   '2026-09-16T04:00:00Z'    one instant, the same for every match on the day
 *
 * `{ offsetMs }` or `{ instant }`, or null for anything else — a spec nobody
 * can read is not quietly treated as the open, because the difference between
 * pricing a day at the open and pricing it two hours in is the whole of what
 * this option is for.
 *
 * An offset is relative to the match's own first capture rather than to the
 * clock, because "the open" is not a time of day: an afternoon game and a night
 * game are first priced hours apart, and the punter's question is how long they
 * waited after the price appeared.
 */
export function parseAt(at) {
  if (typeof at !== 'string' || !at.trim()) return null;
  const text = at.trim();
  const offset = OFFSET_RE.exec(text);
  if (offset) {
    if (!offset[1]) return { offsetMs: 0 };
    const size = Number(offset[2]) * UNIT_MS[offset[3].toLowerCase()];
    return { offsetMs: offset[1] === '-' ? -size : size };
  }
  const instant = Date.parse(text);
  return Number.isFinite(instant) ? { instant } : null;
}

const capturedMs = (p) => {
  const t = Date.parse(p?.capturedAt ?? '');
  return Number.isFinite(t) ? t : null;
};

/**
 * ONE PRICE PER BOOK PER SIDE AS AT A MOMENT: the latest capture at or before
 * it, and nothing at all for a book that had not priced the side yet.
 *
 * A CLOSING ROW IS NEVER A CANDIDATE. Its `capturedAt` is the capture at
 * kickoff, which is after every `open+Nh` a punter could have stood at, and a
 * source that stamps both its opening and its closing row with the kickoff
 * (football-data does) would otherwise hand every "at" query the close — the
 * one price this option exists to avoid betting at. So the pool is the drift:
 * the opening row and every `live` capture the day kept.
 */
function capturesAt(board, spec) {
  const pool = board.filter((p) => p.kind !== 'closing' && capturedMs(p) !== null);
  if (!pool.length) return [];
  const first = Math.min(...pool.map(capturedMs));
  const moment = spec.instant ?? first + spec.offsetMs;

  const chosen = new Map();
  for (const p of pool) {
    const at = capturedMs(p);
    if (at > moment) continue;
    const key = `${p.bookmaker}|${p.selection}`;
    const held = chosen.get(key);
    if (!held || capturedMs(held) <= at) chosen.set(key, p);
  }
  return [...chosen.values()];
}

/**
 * Stored history (server/history/store.mjs `MatchLine[]`) → the same canonical
 * rows the live feed reduces to, so a backtest day and today's pad run through
 * identical code.
 *
 * A season ingested from Betfair carries only `closing` prices; one from
 * football-data carries `opening` and `closing`. Asking for `opening` on the
 * first would silently return nothing, so the fallback is explicit and which
 * one was actually used comes back as `priceBasis` — a backtest that quietly
 * bet the closing line is a backtest that quietly cheated.
 *
 * `priceKind: 'at'` PRICES THE DAY WHERE THE PUNTER WOULD ACTUALLY HAVE BEEN
 * STANDING, which a season with a drift history (F1: every capture the app
 * bought, kept as `kind: 'live'` rows) can now answer. `at` says when — an
 * offset from this match's first capture, or an ISO instant (`parseAt`) — and
 * each book is priced at its latest capture at or before that moment. A match
 * with no capture that early falls back to its opening row, then to
 * `fallbackKind`, and `priceBasis` says which of the three actually happened.
 *
 * `rawEvents` is the same board in The Odds API's event shape, which is the
 * only thing Classic will read (it delegates to `normaliseOdds`).
 */
export function fromHistoryMatches(matches, {
  priceKind = 'opening',
  fallbackKind = 'closing',
  at = null,
  asOf,
} = {}) {
  // An `at` nobody can read is the first capture — "at" with no moment is the
  // open — and `parseAt` still answers null so a caller can say so out loud
  // instead (`backtest.js` puts it in the run's notes).
  const atSpec = priceKind === 'at' ? (parseAt(at) ?? { offsetMs: 0 }) : null;
  const rows = [];
  const rawEvents = [];
  const kinds = new Set();

  for (const stored of matches ?? []) {
    // A STORED RACE IS KEPT PACKED (`race-line.js`) and is expanded HERE, one
    // day's worth at a time, because a season of them is 21,695 races and
    // nothing should ever hold that expanded.
    const match = asMatchLine(stored);
    // A STORED RACE IS A STORED MATCH, with two differences and no others: its
    // market is `win` rather than `h2h`, and its sides are its own runners
    // rather than the three a head-to-head can carry. `match.runners` is the
    // one field that says which kind of line this is — the same tell
    // `strategy.js` and `outliers.js` read — so a fixture comes out of here
    // byte-for-byte what it always was.
    const race = !!match?.runners;
    const market = marketOf(match);
    const selections = race ? (match.selections ?? []) : SELECTIONS;
    const board = (match?.prices ?? []).filter((p) => (p.market ?? 'h2h') === market);
    let kind = priceKind;
    let picked = atSpec ? capturesAt(board, atSpec) : board.filter((p) => p.kind === priceKind);
    // A moment this match has no capture for is not a reason to bet the close:
    // the opening row is the earliest price anybody could have taken, and it is
    // asked for first. `priceBasis` says which one answered.
    if (picked.length === 0 && atSpec) {
      picked = board.filter((p) => p.kind === 'opening');
      if (picked.length) kind = 'opening';
    }
    if (picked.length === 0 && fallbackKind && fallbackKind !== kind) {
      picked = board.filter((p) => p.kind === fallbackKind);
      kind = fallbackKind;
    }
    if (picked.length === 0) continue;
    kinds.add(kind);

    // First row per bookmaker x selection wins, so a season with two captures
    // of the same kind cannot put two prices on one outcome.
    const books = new Map();
    // The race bundle, read once per match and repeated onto every row it
    // writes — `buildUniverse` copies it off the first row it sees for a key,
    // exactly as `src/lib/racing/board.js` feeds it from the live card.
    // Built for EVERY match, not only a race: a fixture carries no race fields
    // and the bundle comes back empty for one, but it may carry a features bag
    // (`MATCH_FIELDS`), and a backtest that dropped those would be a backtest of
    // a model with nothing to read.
    const bundle = raceBundle(match);
    for (const p of picked) {
      if (!selections.includes(p.selection)) continue;
      rows.push({
        matchKey: match.matchKey,
        providerEventId: match.matchKey,
        sport: match.sport,
        competition: match.competition,
        kickoff: match.kickoff,
        home: match.home ? { ...match.home } : null,
        away: match.away ? { ...match.away } : null,
        selection: p.selection,
        bookmaker: p.bookmaker,
        bookmakerName: p.bookmaker,
        price: p.price,
        liquidity: p.liquidity ?? null,
        capturedAt: p.capturedAt ?? asOf ?? null,
        kind,
        ...(bundle ?? {}),
      });
      const book = books.get(p.bookmaker) ?? new Map();
      if (!book.has(p.selection)) book.set(p.selection, p.price);
      books.set(p.bookmaker, book);
    }

    // Classic is the only reader of `rawEvents` and Classic is a two-team
    // product: The Odds API event shape has a home team and an away team and
    // nowhere to put a fourteen-runner field. A race is left out of it rather
    // than faked into it; every racing strategy reads `universe.matches`.
    if (race) continue;

    const bookmakers = [];
    for (const [key, sides] of books) {
      const outcomes = [];
      if (sides.has('home')) outcomes.push({ name: match.home?.name, price: sides.get('home') });
      if (sides.has('away')) outcomes.push({ name: match.away?.name, price: sides.get('away') });
      if (sides.has('draw')) outcomes.push({ name: 'Draw', price: sides.get('draw') });
      if (outcomes.length === 0) continue;
      bookmakers.push({ key, title: key, markets: [{ key: 'h2h', outcomes }] });
    }
    if (bookmakers.length === 0) continue;

    rawEvents.push({
      id: match.matchKey,
      sport_key: SPORTS[match.sport]?.oddsApiKeys?.[0] ?? match.sport,
      sport_title: match.competition,
      commence_time: match.kickoff,
      home_team: match.home?.name,
      away_team: match.away?.name,
      bookmakers,
    });
  }

  const priceBasis = kinds.size === 0 ? priceKind : kinds.size === 1 ? [...kinds][0] : 'mixed';
  return { rows, rawEvents, priceBasis };
}

/**
 * WHAT A RACE BRINGS WITH IT that a fixture does not.
 *
 * A canonical row is otherwise flat — sport, competition, kickoff, a side and a
 * price — because a fixture has nothing else to carry: `home` and `away` say
 * the whole of it. A race does. Its ACTIVE FIELD (`selections`), its runners,
 * the wider overround band a fourteen-runner market sums to, and the feed's own
 * word for whether it has jumped are all facts about the race rather than about
 * any one price, and every one of them is read downstream — `selectionsOf` and
 * `overroundBandFor` here, `sideName` in outliers.js, `describe` in strategy.js,
 * `ticketState` in timeline.js.
 *
 * So the bundle is copied off the FIRST row for a match key and nowhere else.
 * `src/lib/racing/board.js` repeats it on every row it writes for exactly this
 * moment; a fixture row carries none of these keys, so a football match comes
 * out of here byte-for-byte what it always was.
 */
export const RACE_FIELDS = [
  'selections', 'runners', 'overroundBand', 'sportTitle',
  'raceLabel', 'venue', 'venueId', 'raceNumber', 'raceName',
  'distance', 'trackCondition', 'placesPaid',
  'marketClosedAt', 'inplayAt', 'category', 'country',
];

/**
 * EVERYTHING THAT IS A FACT ABOUT THE MATCH RATHER THAN ABOUT A PRICE.
 *
 * The race fields, and the FEATURES BAG beside them (`engine/features.js`):
 * `{ squiggle: { tips: [...] } }` and whatever else somebody published free
 * about this fixture. A race carries its field; a fixture can now carry a
 * crowd of tipsters; both travel the same way, off the first row for a key,
 * because both are one fact repeated onto every price row of a match.
 *
 * A row with no features is untouched, which is every row this app built before
 * the daily features job existed and every row of a sport nobody publishes for.
 */
export const MATCH_FIELDS = [...RACE_FIELDS, 'features'];

/** The match fields a stored line carries, ready to ride on every one of its rows. */
export function raceBundle(match) {
  const out = {};
  for (const field of MATCH_FIELDS) {
    if (match?.[field] !== undefined && match?.[field] !== null) out[field] = match[field];
  }
  return out;
}

/**
 * HOW OLD A QUOTE MAY BE AND STILL BE A QUOTE.
 *
 * `capturedAt` was written onto every row from the day rows existed and read by
 * nothing (engine review #9): a price fetched four hours ago voted in the
 * consensus at full weight and could be the figure printed on a ticket. A board
 * is not a photograph of one moment — a provider hands back whatever each book
 * last published — so the age is per ROW, not per snapshot.
 *
 * Three numbers, because three markets go off at three speeds. A football
 * market barely moves in an afternoon; a race is repriced all the way to the
 * jump, and the app's own racing floor is minutes not hours; an exchange price
 * is an OFFER, and a thirty-minute-old offer is a price nobody is standing
 * behind any more.
 */
export const STALE_TTL_MS = {
  default: 6 * 60 * 60 * 1000,
  racing: 90 * 60 * 1000,
  exchange: 30 * 60 * 1000,
};

/** The TTL this row is held to: the exchange's, the code's, or the default. */
export function ttlFor(sport, bookmaker) {
  if (isExchange(bookmaker)) return STALE_TTL_MS.exchange;
  return isRacingSport(sport) ? STALE_TTL_MS.racing : STALE_TTL_MS.default;
}

/**
 * How old this row is at `asOf`, in ms — or null where the question does not
 * apply and the row keeps full weight.
 *
 * Null for a row with no `capturedAt` (an old board, stored before the field
 * existed) and for anything that is not a LIVE quote. A stored opening or
 * closing price is a fact about a day that has already happened, captured
 * whenever the ingest got to it; holding it to a six-hour TTL against a
 * backtest's own cutoff would throw away every season in the history.
 */
export function rowAge(row, asOfMs) {
  if (row?.kind !== 'live' || !Number.isFinite(asOfMs)) return null;
  const at = Date.parse(row?.capturedAt ?? '');
  if (!Number.isFinite(at)) return null;
  return asOfMs - at;
}

/**
 * The weight a quote of this age carries in the consensus: 1 when it was just
 * read, falling in a straight line to 0.5 at the TTL and no further — past the
 * TTL it is not in the universe at all (`buildUniverse` drops it), and a vote
 * that decayed to zero would leave the market with no fair price rather than an
 * older one.
 */
export function ageWeight(ageMs, ttlMs) {
  if (!Number.isFinite(ageMs) || !(ttlMs > 0) || ageMs <= 0) return 1;
  return 1 - 0.5 * Math.min(1, ageMs / ttlMs);
}

/**
 * Rows → one Match per fixture, keyed by matchKey. Eligibility mirrors
 * `buildPool` in tickets.js: the match must start after `asOf`, and (unless
 * `sameDay` is off) on `forDate` in `timeZone`. `rawEvents` rides along
 * untouched so the Classic wrapper can hand them straight to `normaliseOdds`.
 *
 * A LIVE ROW OLDER THAN ITS TTL NEVER GETS IN (`ttlFor` above). It is dropped
 * here rather than filtered downstream because everything downstream — the
 * board cell, the consensus, the best price a ticket is written at — reads the
 * universe and would each have to remember to ask.
 */
export function buildUniverse(rows, {
  forDate,
  asOf,
  timeZone = 'Australia/Sydney',
  sameDay = true,
  rawEvents = [],
} = {}) {
  const cutoff = new Date(asOf).getTime();
  const matches = new Map();
  let stale = 0;
  for (const row of rows ?? []) {
    if (!Number.isFinite(row.price) || row.price <= 1) continue;
    const age = rowAge(row, cutoff);
    if (age !== null && age > ttlFor(row.sport, row.bookmaker)) { stale += 1; continue; }
    const start = new Date(row.kickoff).getTime();
    if (!(start > cutoff)) continue;
    if (sameDay && localDate(start, timeZone) !== forDate) continue;
    let match = matches.get(row.matchKey);
    if (!match) {
      match = {
        matchKey: row.matchKey,
        sport: row.sport,
        competition: row.competition,
        kickoff: row.kickoff,
        providerEventId: row.providerEventId ?? null,
        home: { ...row.home },
        away: { ...row.away },
        // WHEN THIS BOARD WAS CUT, carried on the match itself so that
        // `consensus` can age-weight a vote without every caller in the app
        // having to learn to pass a clock down. A bare match built by hand in a
        // test has none, and is weighted the way it always was.
        asOf: asOf ?? null,
        prices: {},
      };
      for (const field of MATCH_FIELDS) {
        if (row[field] !== undefined && row[field] !== null) match[field] = row[field];
      }
      // WHAT A LEG OFF THIS MATCH CALLS ITS SPORT. For a fixture that is the
      // competition ("AFL") and it always has been. For a race the competition
      // is the MEETING — "Randwick 2026-09-15" — which the race label beside it
      // already says, twice as long; the code's own name is what belongs there.
      // Derived from the one field that makes a match a race rather than being
      // repeated onto every price row.
      if (match.runners && !match.sportTitle) match.sportTitle = sportLabel(match.sport);
      matches.set(row.matchKey, match);
    }
    (match.prices[row.selection] ??= []).push({
      bookmaker: row.bookmaker,
      bookmakerName: row.bookmakerName,
      price: row.price,
      // The book's own URL for this bet, carried from the row so a leg printed
      // off this board can hand the punter to the page that quoted it
      // (`legLink`, lib/bookmakers.js). Absent on a row that had none, which is
      // every row from a book that publishes nothing.
      ...(row.link ? { link: row.link, linkKind: row.linkKind ?? 'event' } : {}),
      // WHAT THIS BOOK OPENED AT, and when, carried from the row so a ticket
      // can print the move since the open (`openingPriceFor`, lib/live-prices.js).
      // Absent where the board carries none, which is every board stored before
      // the day's drift series existed.
      ...(Number.isFinite(row.openingPrice)
        ? { opening: row.openingPrice, openingAt: row.openingAt ?? null }
        : {}),
      liquidity: row.liquidity ?? null,
      capturedAt: row.capturedAt,
      kind: row.kind,
    });
  }
  return { forDate, asOf, timeZone, sameDay, rawEvents, matches, size: matches.size, stale };
}

/** The longest price on the board for a selection. First book wins a tie. */
export function bestPrice(match, selection) {
  let best = null;
  for (const row of match.prices?.[selection] ?? []) {
    if (!best || row.price > best.price) best = { bookmaker: row.bookmaker, price: row.price };
  }
  return best;
}

/**
 * A BOOK A PUNTER CAN ACTUALLY WALK INTO, at its best price.
 *
 * `bestPrice` shops the whole board, exchange included, which is right for a
 * multi that is only ever a price comparison. A single is an instruction to go
 * and place a bet, and the exchange is not a bookmaker: its price is a
 * standing offer that may not be there, in a size that may not be yours, and
 * it charges commission on the win, so the number printed is not the number
 * paid. Ties go to the first book listed, as `bestPrice` does.
 *
 * It lives beside `bestPrice` rather than in the one strategy that first
 * needed it, because "what could I have got for this leg?" is a question about
 * the BOARD — the singles leg rule asks it, and so does the margin line on a
 * multi (`lib/margin.js`). Two copies of the exclusion list would be two
 * different answers to the same question.
 */
export const NOT_A_BET_BOOK = ['betfair_ex_au'];

export function bestPriceToTake(match, selection) {
  let best = null;
  for (const row of match.prices?.[selection] ?? []) {
    if (NOT_A_BET_BOOK.includes(row.bookmaker)) continue;
    if (!best || row.price > best.price) best = { bookmaker: row.bookmaker, price: row.price };
  }
  return best;
}

/** One bookmaker's price for a selection, or null if it isn't pricing it. */
export function priceAt(match, selection, bookmaker) {
  const row = match.prices?.[selection]?.find((r) => r.bookmaker === bookmaker);
  return row ? row.price : null;
}

/**
 * The selections this market CAN carry, in the order devig sees them.
 *
 * A head-to-head has three and they are the same three every time, so they are
 * the default. A RACE carries its own — `['r1','r2',…]`, its ACTIVE field, with
 * the scratchings already removed at ingest (`src/lib/racing/`). That removal is
 * the whole of why a scratched runner cannot break anything downstream: the
 * books go on quoting it, the rows are still on the match, and nothing here
 * ever looks at them, so a book that prices the field that is actually running
 * is a complete vector and votes like any other.
 */
export function selectionsOf(match) {
  return match?.selections ?? SELECTIONS;
}

/**
 * Selections this match actually has prices for, in `selectionsOf` order.
 *
 * Filtered rather than taken whole, and deliberately: a runner no book has
 * priced would make EVERY book's vector incomplete, and consensus would answer
 * null for the whole race. Completeness is "every selection in `selectionsOf`
 * that the board has an opinion on at all".
 */
function pricedSelections(match) {
  return selectionsOf(match).filter((s) => (match.prices?.[s]?.length ?? 0) > 0);
}

/**
 * The band an h2h vector's implied probabilities may sum to and still be a
 * market. Outside it the book is not pricing the match: a 1000 placeholder on
 * an exchange with no offer, a stale line, a one-sided quote. Such a vector
 * devigs to nonsense — one 1000/10 row in AFL 2025 put a home win at 0.0000
 * and cost more log loss than the other 215 matches combined.
 */
export const OVERROUND_BAND = [0.85, 1.35];

/**
 * The band for THIS market. A two-way book rounds to about 105%; a twelve-runner
 * race is routinely 120% and a small field can sit under 100% once the exchange
 * is in, so a race carries its own `overroundBand` ([0.95, 1.6]) and the h2h
 * band would throw the whole card away.
 */
export const overroundBandFor = (match) => match?.overroundBand ?? OVERROUND_BAND;

function saneVector(vector, band = OVERROUND_BAND) {
  const sum = vector.reduce((acc, p) => acc + 1 / p, 0);
  return sum >= band[0] && sum <= band[1];
}

/**
 * WHICH DEVIG A MARKET GETS WHEN NOBODY SAID — and the answer depends on what
 * kind of market it is, which is the bug this constant exists for.
 *
 * The default here was 'power' for a fixture and 'power' for a race, and every
 * caller that did not name a method got it. On a two-way market that is fine:
 * the three methods land within a point or two of each other. On a big field it
 * is not fine at all, and the live ticket that proved it was a Racing all-up
 * over two Albion Park harness races: a $1.20 favourite in a 137% book devigged
 * MULTIPLICATIVELY — 0.833 / 1.37 — prints a 61% chance for a runner the market
 * makes a 4-on favourite, so a two-leg ticket read "Fair $2.71 · pays $1.44
 * (−46.8%)" and "Medium risk · 1 in 3 chance". Both figures were arithmetic,
 * and both were wrong about the same thing: multiplicative and power spread a
 * race's whole round proportionally, so the shortest runner is charged the
 * largest absolute share of a margin it does not carry. Shin models the
 * favourite–longshot bias directly and reads the same runner at about 0.79.
 *
 * So: SHIN ON A RACE, POWER EVERYWHERE ELSE, wherever a caller does not name a
 * method. A caller that names one still gets it — the detector is explicitly on
 * power (`outliers.DEVIG`), the certainty gate explicitly asks all three
 * (`CERTAINTY_METHODS`), and a strategy document says what it screens with. The
 * default is what the app believes when nobody has said otherwise, and on a
 * fourteen-runner field it should not believe the method that fits it worst.
 */
export const defaultDevig = (match) => (isRacingSport(match?.sport) ? 'shin' : 'power');

/**
 * One bookmaker's own fair probability for a selection: its full h2h vector
 * devigged on its own overround. This is "the de-vigged market at the time of
 * the bet" — what the price you are actually taking is worth once the margin
 * is stripped — and the honest baseline for edge. Null if that book does not
 * price every side of the match.
 *
 * `method` unnamed is `defaultDevig(match)` — Shin on a race, power on a
 * fixture. See the note on that constant for the ticket that made it necessary.
 */
export function bookFair(match, selection, bookmaker, method = null) {
  const sides = pricedSelections(match);
  if (!sides.includes(selection)) return null;
  // THE QUOTE, NOT WHAT THE QUOTE PAYS, and deliberately — see the note on
  // `consensus` below. This function is always paired with the PRICE it is
  // about: a candidate's `pFair` sits beside the price the ticket prints, and
  // `edge`/`ev` subtract one from the other. Devigging a net vector against a
  // gross price would hand an exchange leg the commission as edge. The place
  // the exchange is read as an OPINION rather than as a price — `consensus`,
  // and `bookFairNet` for the sharp anchor — takes the commission off.
  const vector = sides.map((side) => match.prices[side].find((r) => r.bookmaker === bookmaker)?.price);
  if (vector.some((p) => p === undefined) || !saneVector(vector, overroundBandFor(match))) return null;
  const { probs } = devig(vector, method ?? defaultDevig(match));
  return probs[sides.indexOf(selection)];
}

/**
 * ONE BOOK'S PRICE VECTOR PER MATCH, BUILT ONCE.
 *
 * `consensus` is asked the same question once per selection, twice over for a
 * detector pass (the whole market, then the market without the flagged book),
 * and every one of those calls used to walk every row on the match to rebuild
 * the same map. Two sides is nothing; a fourteen-runner race priced by fourteen
 * books is ~200 rows walked twenty-eight times for one race.
 *
 * The map is the same for every caller because `exclude` is applied at the VOTE,
 * not at the build: each book's entry is built from its own rows and nothing
 * else, so dropping one key afterwards leaves exactly what building without it
 * would have left. The keys are sorted here for the same reason they were sorted
 * before — floating-point addition is not associative, so the mean must not
 * depend on the order the rows arrived in.
 *
 * Keyed on the match OBJECT (weakly, so a replaced board is collectable) and
 * guarded by a fingerprint of the priced sides and how many rows they hold, so
 * an appended exchange column cannot be answered from a stale map.
 */
const VECTOR_CACHE = new WeakMap();

function buildBookVectors(match, sides, commission) {
  const asOfMs = Date.parse(match?.asOf ?? '');
  const books = new Map();
  for (const side of sides) {
    for (const row of match.prices[side]) {
      const entry = books.get(row.bookmaker)
        // `probs` is this book's vector devigged, filled in on first use and
        // keyed by method. One vector devigs to one set of probabilities for
        // the WHOLE match, and `consensus` is asked about one selection at a
        // time — so a fourteen-runner race priced by fourteen books used to
        // bisect the same power root 196 times where it needs 14. It is on the
        // entry rather than beside it because the entry is what the cache
        // holds, and the fingerprint already covers the sides and the
        // commission the vector was built from.
        ?? { prices: {}, liquidity: {}, probs: {}, age: null, dated: true, bookmaker: row.bookmaker };
      if (entry.prices[side] === undefined) {
        // The price the book actually pays, deflated once, here — so the whole
        // market votes in the same currency (engine review #14).
        entry.prices[side] = netPrice(row.price, row.bookmaker, commission);
        entry.liquidity[side] = row.liquidity;
        // A BOOK IS AS OLD AS ITS OLDEST SIDE. Its vote is one devig of one
        // vector, and a vector half of which was read four hours ago is a
        // four-hour-old opinion however fresh the other half is.
        const age = rowAge(row, asOfMs);
        if (age === null) entry.dated = false;
        else entry.age = entry.age === null ? age : Math.max(entry.age, age);
      }
      books.set(row.bookmaker, entry);
    }
  }
  return { books, keys: [...books.keys()].sort() };
}

function bookVectors(match, sides, commission) {
  if (!match || typeof match !== 'object') return buildBookVectors(match, sides, commission);
  let rows = 0;
  for (const side of sides) rows += match.prices[side].length;
  const fingerprint = `${sides.join('|')}#${rows}#${commission}`;
  const cached = VECTOR_CACHE.get(match);
  if (cached && cached.fingerprint === fingerprint) return cached;
  const built = { fingerprint, ...buildBookVectors(match, sides, commission) };
  VECTOR_CACHE.set(match, built);
  return built;
}

// --- what a vote is worth ------------------------------------------------------
//
// THE BOARD IS NOT THIRTEEN OPINIONS (engine review #5). `consensus` averaged
// every book that priced the match, one vote each, and two things were wrong
// with that at once. Ladbrokes and Neds are one trading room and publish the
// same line twice, TAB and TABtouch are one Tabcorp feed — so the mean drifted
// toward whichever operator ran the most brands. And the exchange, the only
// price on the board that moves because somebody's money moved it, got the same
// one thirteenth as the shop that copied its line an hour ago.
//
// Two rules, and between them they are the whole of the weighting:
//
//   1. BY CLASS. A sharp voter — the exchange, or Pinnacle — is worth more than
//      the soft board put together, because it is the price the soft board is
//      copying. One of them present takes 0.6 and the soft board shares 0.4;
//      both present take 0.45 each and the soft board keeps the 0.10 left. The
//      two can never add past 1.0, because the soft share is what is LEFT
//      rather than a third constant that could be set to disagree with them.
//   2. ONE VOTE PER OPERATOR, inside the soft share (`operatorOf`,
//      lib/bookmakers.js). Ladbrokes and Neds get half an operator's vote each,
//      so together they count once.
//
// Age (#9) multiplies whatever comes out of those, and the weights are
// normalised at the end, so a board with no soft books at all is still a whole
// vote rather than 60% of one.

/** Books that trade on money rather than on margin, and are weighted like it. */
export const SHARP_BOOKS = ['pinnacle'];

/** Is this the one bookmaker the app treats as sharp? (The exchange is its own class.) */
export const isSharpBook = (bookmaker) => SHARP_BOOKS.includes(bookmaker);

/**
 * WHERE A SHARP REFERENCE PRICES THE SIDE, IT IS THE FAIR PRICE — alone (F1b).
 *
 * The reviewer's sentence for it: five Australian books are one opinion in five
 * coats. Most of them buy the same feed, and a weighted mean over thirteen
 * brands that copy each other is not thirteen opinions however carefully the
 * weights are set — it is one opinion with a spuriously tight error bar, and
 * the one price on the board that moved because somebody's money moved it gets
 * averaged INTO it. E2 gave the exchange 60% of the vote; this gives it the
 * whole of it, and the local board becomes what is read when there is no sharp
 * price at all.
 *
 * "Sharp" is the exchange with money behind it — `betfair_ex_au`, and where the
 * feed reports size (the live app key does; the delayed one does not) that size
 * must be above zero — or Pinnacle. The exchange's price votes NET OF
 * COMMISSION exactly as it always did: it is an opinion about a probability,
 * and the commission is part of what the opinion pays.
 *
 * `consensus` reports which of the two happened as `mode`, and the screens say
 * it in words: "fair from the exchange" or "fair from local books only".
 *
 * It is the app's answer everywhere — nothing on a screen and no strategy
 * chooses it, because two fair prices on one board is the bug this removes.
 * `sharpOnly: false` is still accepted by `consensus`, the way `weighting:
 * 'flat'` is: E2's weighted board stays reachable so it can be measured against
 * this one rather than only remembered.
 */
export const SHARP_ONLY = true;

/** What a sharp voter is worth when it is the only one on the board. */
export const SHARP_WEIGHT = 0.6;

/** What each is worth when the exchange AND Pinnacle are both pricing the match. */
export const SHARP_WEIGHT_EACH = 0.45;

/** 'exchange' | 'sharp' | 'soft' — which of the three weights this book draws on. */
export function bookClass(bookmaker) {
  if (isExchange(bookmaker)) return 'exchange';
  if (isSharpBook(bookmaker)) return 'sharp';
  return 'soft';
}

/**
 * Voter key → its share of the vote, before age, summing to 1 across `voters`.
 *
 * `voters` is a list of bookmaker keys. Pure arithmetic on that list and
 * nothing else, so the explain sheet and the consensus cannot disagree about
 * who counted for how much.
 */
function classShares(voters) {
  const byClass = { exchange: [], sharp: [], soft: [] };
  for (const key of voters) byClass[bookClass(key)].push(key);
  const both = byClass.exchange.length > 0 && byClass.sharp.length > 0;
  const each = both ? SHARP_WEIGHT_EACH : SHARP_WEIGHT;
  const exchangeShare = byClass.exchange.length ? each : 0;
  const sharpShare = byClass.sharp.length ? each : 0;
  const softShare = Math.max(0, 1 - exchangeShare - sharpShare);

  const shares = new Map();
  for (const key of byClass.exchange) shares.set(key, exchangeShare / byClass.exchange.length);
  for (const key of byClass.sharp) shares.set(key, sharpShare / byClass.sharp.length);

  // ONE VOTE PER OPERATOR, split between that operator's brands.
  const operators = new Map();
  for (const key of byClass.soft) {
    const operator = operatorOf(key);
    if (!operators.has(operator)) operators.set(operator, []);
    operators.get(operator).push(key);
  }
  const perOperator = operators.size ? softShare / operators.size : 0;
  for (const brands of operators.values()) {
    for (const key of brands) shares.set(key, perOperator / brands.length);
  }
  return shares;
}

/**
 * The books eligible to vote on this match, in sorted key order, each with the
 * devigged vector it votes with and the age weight it carries.
 *
 * Shared by `consensus` and `consensusWeights` so that "who voted" is answered
 * once. A book quoting only part of the match, or a vector outside the
 * overround band, is not a voter at all and never reaches here.
 */
function voters(match, sides, { method, exclude, commission, band }) {
  const { books, keys } = bookVectors(match, sides, commission);
  const out = [];
  for (const key of keys) {
    if (key === exclude) continue;
    const entry = books.get(key);
    // Completeness, and on a race that means the ACTIVE field: a book pricing
    // eleven of the eleven runners still in the race is a whole opinion, and
    // the scratching it goes on quoting is not part of the question.
    if (sides.some((side) => entry.prices[side] === undefined)) continue;
    const vector = sides.map((side) => entry.prices[side]);
    if (!saneVector(vector, band)) continue;
    entry.probs[method] ??= devig(vector, method).probs;
    out.push({
      bookmaker: key,
      probs: entry.probs[method],
      liquidity: entry.liquidity,
      age: entry.dated ? ageWeight(entry.age, ttlFor(match?.sport, key)) : 1,
    });
  }
  return out;
}

/**
 * THE SHARP REFERENCE ON THIS SIDE, or null — the exchange first, then Pinnacle.
 *
 * `votes` is what `voters` returned, so a book that prices only half the match
 * or quotes an insane vector is already not here. The extra test is SIZE: where
 * the feed says how much money is on the exchange's price, a price with none
 * behind it is a quote nobody has backed and is not a sharp reference at all
 * (most Australian racing money arrives inside the last four minutes). Where it
 * says nothing — the delayed Betfair key reports no volume — being on the board
 * is the whole of what can be asked.
 */
function sharpVote(votes, selection) {
  const backed = (v) => {
    const size = v.liquidity?.[selection];
    return !Number.isFinite(size) || size > 0;
  };
  return votes.find((v) => bookClass(v.bookmaker) === 'exchange' && backed(v))
    ?? votes.find((v) => bookClass(v.bookmaker) === 'sharp')
    ?? null;
}

/**
 * The market's fair probability for a selection: every bookmaker that prices
 * the whole match gets devigged on its own book, and their fair probabilities
 * are averaged — weighted by class, by operator and by age (see the block
 * above). A book quoting only one side would devig against an incomplete
 * vector, so it is ignored rather than guessed at.
 *
 * `exclude` drops one bookmaker key — a best-price strategy uses it so the
 * book it is betting at does not vote on its own fairness.
 *
 * THREE CORRECTIONS THE VOTES GO THROUGH FIRST, and all of them are here rather
 * than in the callers so that every consumer of "what does the market think
 * this is worth" gets the same answer (engine review #14, #9 and #5):
 *
 *   · an exchange votes NET OF COMMISSION, because that is what it pays. It
 *     was deflated in `sharpAnchor` and in `outliers` and NOT here — so the
 *     one function every strategy and every board cell reads was the one that
 *     believed the quote, and a match had two fair prices depending on which
 *     door you came in by. A vote is an opinion about a probability, so the
 *     correction belongs on it; `bookFair` above, which is always paired with
 *     a printed price, deliberately does not make it;
 *   · a quote votes in proportion to how fresh it is, falling to half a vote at
 *     its TTL (`ageWeight`). A row with no `capturedAt`, and every stored
 *     opening or closing price, carries a full vote as it always did;
 *   · a vote is worth what its CLASS and its OPERATOR are worth. `weighting`
 *     is still spelled 'equal' and still means what a punter would mean by it —
 *     every independent opinion counts the same — which is exactly what one
 *     vote per operator restores. 'flat' is the old arithmetic, one vote per
 *     brand, kept for a backtest that wants to compare against it; 'liquidity'
 *     is unchanged and still only fires where every row carries size.
 *
 * `devig` unnamed is `defaultDevig(match)` — Shin on a race, power on a
 * fixture.
 */
export function consensus(match, selection, {
  devig: method = null,
  weighting = 'equal',
  exclude = null,
  commission = COMMISSION,
  sharpOnly = SHARP_ONLY,
} = {}) {
  const sides = pricedSelections(match);
  if (!sides.includes(selection)) return null;
  const votes = voters(match, sides, {
    method: method ?? defaultDevig(match), exclude, commission, band: overroundBandFor(match),
  });
  if (votes.length === 0) return null;

  const index = sides.indexOf(selection);

  // SHARP FIRST (F1b). One price, net of commission, and the local board is not
  // averaged into it. `books` is still how many shops priced the match, because
  // that is what every thin-market floor in the app is counting; `mode` is what
  // the fair price was actually read off.
  const sharp = sharpOnly && weighting !== 'flat' ? sharpVote(votes, selection) : null;
  if (sharp) {
    return {
      pFair: sharp.probs[index], books: votes.length, mode: 'sharp', source: sharp.bookmaker,
    };
  }

  const byLiquidity = weighting === 'liquidity'
    && votes.every((v) => Number.isFinite(v.liquidity[selection]) && v.liquidity[selection] > 0);
  const shares = byLiquidity || weighting === 'flat'
    ? null
    : classShares(votes.map((v) => v.bookmaker));
  const weight = (v) => {
    const base = byLiquidity ? v.liquidity[selection] : (shares ? shares.get(v.bookmaker) : 1);
    return base * v.age;
  };
  const total = votes.reduce((sum, v) => sum + weight(v), 0);
  if (!(total > 0)) return null;
  const pFair = votes.reduce((sum, v) => sum + v.probs[index] * weight(v), 0) / total;
  return { pFair, books: votes.length, mode: 'local', source: null };
}

/**
 * WHO DECIDED THIS MATCH'S FAIR PRICE, AND BY HOW MUCH — the explain sheet's
 * half of the weighting above.
 *
 * One row per voter, heaviest first, each with the class it drew its share
 * from, the operator it shares that share with, the age weight it carried and
 * the `weight` it actually voted with, normalised so the column adds to 1. It
 * is a fact about the MATCH rather than about one side of it: the voters are
 * the books that price the whole market, and the shares they draw do not
 * depend on which runner is being asked about.
 *
 * Exported so that a screen can show the sentence rather than assert it, and so
 * that the glossary's "Fair price" entry can be built out of the same numbers
 * instead of retyping them.
 */
export function consensusWeights(match, {
  devig: method = null,
  exclude = null,
  commission = COMMISSION,
  selection = null,
  sharpOnly = SHARP_ONLY,
} = {}) {
  const sides = pricedSelections(match);
  if (sides.length === 0) return [];
  const votes = voters(match, sides, {
    method: method ?? defaultDevig(match), exclude, commission, band: overroundBandFor(match),
  });
  if (votes.length === 0) return [];

  // SHARP FIRST, AND THE SHEET SAYS SO. Where a sharp reference prices the side
  // it is the whole of the fair price (F1b), so it is the whole of the column
  // too: one voter at 1, every other book at 0, and `mode` on the list saying
  // which of the two arithmetics produced it. A zero-weight row is kept rather
  // than dropped — "TAB is on this match and did not vote" is the fact the
  // sheet exists to show, and a list of one book would read as a thin market.
  const sharp = sharpOnly ? sharpVote(votes, selection ?? sides[0]) : null;
  const shares = sharp ? null : classShares(votes.map((v) => v.bookmaker));
  const rows = votes.map((v) => ({
    bookmaker: v.bookmaker,
    operator: operatorOf(v.bookmaker),
    class: bookClass(v.bookmaker),
    share: sharp ? (v.bookmaker === sharp.bookmaker ? 1 : 0) : shares.get(v.bookmaker),
    age: v.age,
    weight: sharp
      ? (v.bookmaker === sharp.bookmaker ? 1 : 0)
      : shares.get(v.bookmaker) * v.age,
  }));
  const total = rows.reduce((sum, r) => sum + r.weight, 0);
  if (total > 0) for (const row of rows) row.weight /= total;
  rows.sort((a, b) => b.weight - a.weight || a.bookmaker.localeCompare(b.bookmaker));
  // A fact about the whole list rather than about any one row, and not
  // enumerable: the rows ARE the sheet, and a `toEqual` on them should still
  // see a plain list of voters.
  Object.defineProperty(rows, 'mode', { value: sharp ? 'sharp' : 'local', enumerable: false });
  return rows;
}
